import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";

const CityAutocomplete = ({ value, onChange, placeholder = "Ville ou code postal...", "data-testid": testId }) => {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const search = async (text) => {
    if (text.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const isPostal = /^\d{2,5}$/.test(text.trim());
      const url = isPostal
        ? `https://geo.api.gouv.fr/communes?codePostal=${text.trim()}&fields=nom,codesPostaux,codeDepartement,population&limit=15`
        : `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(text.trim())}&fields=nom,codesPostaux,codeDepartement,population&boost=population&limit=10`;
      const res = await fetch(url);
      const data = await res.json();

      const items = [];
      for (const c of data) {
        for (const cp of (c.codesPostaux || [])) {
          if (!isPostal || cp.startsWith(text.trim())) {
            items.push({
              label: `${c.nom} (${cp})`,
              city: c.nom,
              postalCode: cp,
              dept: c.codeDepartement,
              population: c.population || 0
            });
          }
        }
      }
      items.sort((a, b) => b.population - a.population);
      setResults(items.slice(0, 12));
      setOpen(items.length > 0);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 250);
  };

  const handleSelect = (item) => {
    const selected = `${item.city} (${item.postalCode})`;
    setQuery(selected);
    onChange(selected);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <Input
        value={query}
        onChange={handleInput}
        onFocus={() => { if (results.length > 0) setOpen(true); }}
        placeholder={placeholder}
        data-testid={testId}
        autoComplete="off"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto">
          {results.map((item, i) => (
            <div
              key={`${item.postalCode}-${item.city}-${i}`}
              className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted transition-colors"
              onClick={() => handleSelect(item)}
              data-testid={`city-option-${i}`}
            >
              <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="font-medium">{item.city}</span>
              <span className="text-muted-foreground">({item.postalCode})</span>
              <span className="text-xs text-muted-foreground ml-auto">Dép. {item.dept}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CityAutocomplete;
